/**
 * @module main
 *
 * Application entry point.
 *
 * Responsibilities, in order:
 *
 *  1. Load the stylesheets.
 *  2. Install global error handlers *first*, so failures during startup are
 *     captured rather than escaping silently.
 *  3. Register the GSAP plugins once.
 *  4. Create the shared scroll controller.
 *  5. Initialise every component through `safeInit`, so one broken section
 *     cannot take down the rest of the page.
 *  6. Signal readiness (which releases the pre-animation CSS lock).
 *
 * The module returns a teardown function used by Vite's HMR to avoid piling up
 * ScrollTriggers and tweens during development.
 */

import "./styles/index.css";

import { registerPlugins, ScrollTrigger, killAllScrollTriggers } from "./core/gsap-setup.js";
import { createScrollController } from "./core/smooth-scroll.js";
import { installGlobalErrorHandlers, safeInit, reportError, getErrorLog } from "./core/errors.js";
import { notify } from "./core/toast.js";

import { initAmbient } from "./components/ambient.js";
import { initHeader } from "./components/header.js";
import { initProgressRail } from "./components/progressRail.js";
import { initSearch } from "./components/search.js";
import { initHero } from "./components/hero.js";
import { initIntro } from "./components/intro.js";
import { initAdmOrbit } from "./components/admOrbit.js";
import { initJourney } from "./components/journey.js";
import { initAiLayer } from "./components/aiLayer.js";
import { initCapabilities } from "./components/capabilities.js";
import { initGovernance } from "./components/governance.js";
import { initCases } from "./components/cases.js";
import { initInsights } from "./components/insights.js";
import { initTrends } from "./components/trends.js";
import { initServices } from "./components/services.js";
import { initFooter } from "./components/footer.js";
import { initReveals } from "./animations/reveal.js";
import { hydrateIconSlots } from "./svg/icons.js";

/** @type {Array<() => void>} */
let teardowns = [];

/**
 * Boot the application.
 *
 * @returns {() => void} Teardown for every component that registered one.
 */
export function boot() {
  /* ---------------------------------------------------------------------- */
  /* 1. Global error handling                                                */
  /* ---------------------------------------------------------------------- */

  const disposeErrorHandlers = installGlobalErrorHandlers();
  teardowns.push(disposeErrorHandlers);

  /* ---------------------------------------------------------------------- */
  /* 2. GSAP                                                                 */
  /* ---------------------------------------------------------------------- */

  registerPlugins();

  /* ---------------------------------------------------------------------- */
  /* 2b. Static icon placeholders                                            */
  /* ---------------------------------------------------------------------- */
  /*
   * The HTML uses `<span data-icon-slot="…">` placeholders so the markup stays
   * readable. Hydrating them first means every icon exists before any component
   * measures the layout.
   */
  safeInit("icon-slots", () => {
    hydrateIconSlots();
  });

  /* ---------------------------------------------------------------------- */
  /* 3. Shared services                                                      */
  /* ---------------------------------------------------------------------- */

  /** @type {import("./core/smooth-scroll.js").ScrollController} */
  let scroll;
  try {
    scroll = createScrollController();
    teardowns.push(() => scroll?.destroy());
    document.documentElement.classList.toggle("smooth-scroll-enabled", scroll.smooth);
  } catch (error) {
    reportError(error, {
      scope: "main:scroll",
      userMessage: "Smooth scrolling is unavailable — standard scrolling is active.",
      severity: "warning",
    });
    // A minimal fallback so dependent components still have something to call.
    scroll = {
      smooth: false,
      scrollTo: (target, options = {}) => {
        const node = typeof target === "string" ? document.querySelector(target) : target;
        if (node instanceof Element) {
          const top = node.getBoundingClientRect().top + window.scrollY - (options.offsetY ?? 0);
          window.scrollTo({ top, behavior: "smooth" });
        } else if (typeof target === "number") {
          window.scrollTo({ top: target, behavior: "smooth" });
        }
      },
      refresh: () => {},
      destroy: () => {},
    };
    teardowns.push(() => {});
  }

  /* ---------------------------------------------------------------------- */
  /* 4. Components                                                           */
  /* ---------------------------------------------------------------------- */
  /*
   * Order matters:
   *  - Ambient, header and the progress rail are chrome; they exist before
   *    anything moves.
   *  - The hero owns the first paint, so it goes before the scroll reveals.
   *  - Sections that inject markup must run before `initReveals`, otherwise the
   *    `[data-reveal]` nodes they add would never be picked up.
   */

  const registry = [
    ["ambient", () => initAmbient()],
    ["header", () => initHeader({ scroll })],
    ["progress-rail", () => initProgressRail()],
    ["search", () => initSearch({ onNavigate: (href) => scroll.scrollTo(href, { offsetY: 90 }) })],
    ["hero", () => initHero()],
    ["intro", () => initIntro()],
    ["adm-orbit", () => initAdmOrbit()],
    ["journey", () => initJourney()],
    ["ai-layer", () => initAiLayer()],
    ["capabilities", () => initCapabilities()],
    ["governance", () => initGovernance()],
    ["cases", () => initCases()],
    ["insights", () => initInsights()],
    ["trends", () => initTrends()],
    ["services", () => initServices()],
    ["footer", () => initFooter()],
    // Reveals last: every section has now injected its own markup.
    ["reveals", () => initReveals()],
  ];

  registry.forEach(([name, factory]) => {
    teardowns.push(safeInit(String(name), /** @type {() => void} */ (factory)));
  });

  /* ---------------------------------------------------------------------- */
  /* 5. Recalculate positions once the layout has settled                    */
  /* ---------------------------------------------------------------------- */

  const settle = () => ScrollTrigger.refresh();

  if (document.fonts?.ready) {
    document.fonts.ready.then(settle).catch(() => {});
  }
  // Injected SVG and web fonts change heights; refresh again after load.
  window.addEventListener("load", settle, { once: true });
  teardowns.push(() => window.removeEventListener("load", settle));

  /*
   * Debug/QA hook. ScrollSmoother drives the scroll position itself, so
   * `window.scrollTo()` is unreliable while it is active — automated checks and
   * support sessions should scroll through `__inmethods.scroll` instead.
   */
  window.__inmethods = {
    scroll,
    refresh: () => ScrollTrigger.refresh(),
  };

  return () => {
    teardowns.forEach((dispose) => {
      try {
        dispose();
      } catch (error) {
        // Teardown must never throw.
        console.warn("[main] teardown error", error);
      }
    });
    teardowns = [];
    killAllScrollTriggers();
  };
}

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                   */
/* -------------------------------------------------------------------------- */

// Remove any previously registered listeners (Vite HMR).
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (typeof window.__appTeardown === "function") window.__appTeardown();
  });
  import.meta.hot.accept();
}

try {
  window.__appTeardown = boot();

  // Release the pre-animation CSS lock. Until this runs, elements marked
  // `data-reveal` are held invisible to prevent a flash of unstyled content.
  window.__reveal?.();

  // Announce availability for analytics, tests and manual debugging.
  window.__appReady = true;
  window.__getErrorLog = getErrorLog;
  document.dispatchEvent(new CustomEvent("app:ready"));
} catch (error) {
  // Last line of defence: show the content rather than a blank page.
  window.__reveal?.();
  reportError(error, {
    scope: "main:boot",
    userMessage: "Part of the experience failed to load. The content is still available below.",
  });
  notify.error("Something went wrong while starting the page.");
}
